/**
 * TanStack Query 키 팩토리
 * 서버 상태 캐시 무효화 전략을 한 곳에서 관리
 *
 * 동적 메타모델 패턴:
 *   ["workspace", workspaceSlug, "entity", ...filters]
 *   - 워크스페이스 단위 캐시 격리 (tenant->workspace 반영)
 *   - 업무유형별 분리: workitem_type code를 키에 포함
 *
 * 하드코딩 모듈별 키(documents/capa/risk...) 대신
 * 동적 업무유형 기반 키 체계로 전환.
 */

export const queryKeys = {
  // -- 서버 업무 날짜(GET /server-date, KST) — 화면의 "오늘" 판정은 클라이언트 시계 대신 이 값 --
  serverDate: ["server-date"] as const,

  // -- 전자서명 이력 (GET /signature-records) --
  signatureRecords: {
    byEntity: (entityId: string | undefined) => ["signature-records", entityId] as const,
  },

  // -- 회사 로고 이미지(objectURL) — logoKey 단위. 로고 교체 시 키가 바뀌어 새로 받음 --
  companyLogo: {
    byKey: (logoKey: string | null) => ["company-logo", logoKey] as const,
  },

  // -- 현재 사용자 (auth/me) --
  me: {
    current: () => ["me"] as const,
  },

  // -- 워크스페이스 --
  workspaces: {
    all: () => ["workspaces"] as const,
    list: (filters?: Record<string, unknown>) => ["workspaces", "list", filters] as const,
    detail: (slug: string) => ["workspaces", slug] as const,
    mine: () => ["workspaces", "mine"] as const,
  },

  // -- 모듈 (13모듈 + 회사 프로비저닝 상태) --
  modules: {
    all: () => ["modules"] as const,
    list: (filters?: Record<string, unknown>) => ["modules", "list", filters] as const,
  },

  // -- 업무유형 (workitem_types) --
  workitemTypes: {
    all: (ws: string) => ["workspace", ws, "workitemTypes"] as const,
    list: (ws: string, filters?: Record<string, unknown>) =>
      ["workspace", ws, "workitemTypes", "list", filters] as const,
    detail: (ws: string, typeId: string) => ["workspace", ws, "workitemTypes", typeId] as const,
  },

  // -- 업무 (workitems) --
  workitems: {
    all: (ws: string) => ["workspace", ws, "workitems"] as const,
    byType: (ws: string, typeCode: string, filters?: Record<string, unknown>) =>
      ["workspace", ws, "workitems", typeCode, "list", filters] as const,
  },

  // -- 아이템 (items) - 동적 메타모델 핵심 --
  items: {
    all: (ws: string) => ["workspace", ws, "items"] as const,
    list: (ws: string, typeCode: string, filters?: Record<string, unknown>) =>
      ["workspace", ws, "items", typeCode, "list", filters] as const,
    byWorkitem: (ws: string, workitemId: string) =>
      ["workspace", ws, "items", "byWorkitem", workitemId] as const,
    detail: (ws: string, itemId: string) => ["workspace", ws, "items", itemId] as const,
    /** 다음 item_no 미리보기 — items.all 아래라 생성 후 무효화로 자동 갱신. */
    nextItemNo: (ws: string, typeId: string) =>
      ["workspace", ws, "items", "nextItemNo", typeId] as const,
    /** 업무 번호 규칙(예: 문서번호) 다음 번호 미리보기 — items.all 아래라 생성·삭제 후 무효화로 자동 갱신. */
    nextValueNo: (ws: string, typeId: string, params: string) =>
      ["workspace", ws, "items", "nextValueNo", typeId, params] as const,
    /** 등록 전 중복 확인(unique_rule) — items.all 아래라 등록·삭제 후 무효화로 자동 갱신. body = 보낸 값 JSON. */
    duplicateCheck: (ws: string, typeId: string, body: string) =>
      ["workspace", ws, "items", "duplicateCheck", typeId, body] as const,
    /** 업무유형별 건수(사이드바 배지) — items.all 아래라 등록·삭제·연계생성·전이 후 무효화로 자동 갱신. */
    typeCounts: (ws: string) => ["workspace", ws, "items", "counts", "type"] as const,
    /** 모듈별 건수(RecordPicker 메뉴 건수) — typeCounts 와 같은 이유로 items.all 아래. */
    moduleCounts: (ws: string) => ["workspace", ws, "items", "counts", "module"] as const,
    relations: (ws: string, itemId: string) =>
      ["workspace", ws, "items", itemId, "relations"] as const,
    /** lang = 필드 단위 값 표시 언어(서버가 그 언어로 풀어 줌). 무효화는 lang 없는 앞부분으로. */
    auditLogs: (ws: string, itemId: string, lang: string) =>
      ["workspace", ws, "items", itemId, "auditLogs", lang] as const,
    deletable: (ws: string, workitemId: string) =>
      ["workspace", ws, "items", "deletable", workitemId] as const,
    deletableOne: (ws: string, itemId: string) =>
      ["workspace", ws, "items", itemId, "deletableOne"] as const,
    references: (ws: string, itemId: string) =>
      ["workspace", ws, "items", itemId, "references"] as const,
    moduleLinkedIds: (ws: string, itemId: string) =>
      ["workspace", ws, "items", itemId, "moduleLinkedIds"] as const,
    /** 업무의 본인 열람 상태 + 서버 오늘 날짜 — items.all 아래라 등록·삭제 후 무효화로 함께 갱신. */
    readState: (ws: string, workitemId: string) =>
      ["workspace", ws, "items", "readState", workitemId] as const,
    /** 업무의 전체 발송 대상 수(활성 구성원). */
    broadcastAudience: (ws: string, workitemId: string) =>
      ["workspace", ws, "items", "broadcastAudience", workitemId] as const,
  },

  // -- 필드 정의 (field_defs) --
  fieldDefs: {
    byType: (ws: string, typeId: string) => ["workspace", ws, "fieldDefs", typeId] as const,
    /** 워크플로우 인스턴스 소유 필드(필드빌더 편집 대상). V72. */
    byWorkflow: (ws: string, workflowId: string) =>
      ["workspace", ws, "fieldDefs", "workflow", workflowId] as const,
    /** 단계(status)×화면(view) 배치 필드 번들 렌더. V72, V84 view(기본 stage_form). */
    stage: (ws: string, workflowId: string, statusId: string, view: string = "stage_form") =>
      ["workspace", ws, "fieldDefs", "stage", workflowId, statusId, view] as const,
  },

  // -- 시스템코드 (codes) --
  codes: {
    // 필드 옵션 해석용(전역+회사사본 병합, use_yn=true)
    byGroup: (ws: string, groupCode: string) => ["workspace", ws, "codes", groupCode] as const,
    // 코드 관리 화면용(회사 스코프 편집 대상, use_yn 무관) — byGroup 과 캐시 분리
    manageByGroup: (ws: string, groupCode: string) =>
      ["workspace", ws, "codesManage", groupCode] as const,
    allGroups: (ws: string) => ["workspace", ws, "codeGroups"] as const,
  },

  // -- 전역 시스템 코드 (시스템 어드민) --
  adminCodes: {
    allGroups: () => ["adminCodes", "groups"] as const,
    byGroup: (groupCode: string) => ["adminCodes", "codes", groupCode] as const,
  },

  // -- 전역 워크플로우 템플릿 (시스템 어드민) --
  adminWorkflows: {
    templates: () => ["adminWorkflows", "templates"] as const,
    byType: (typeId: string, role: string) => ["adminWorkflows", "workflow", typeId, role] as const,
  },

  // -- 워크플로우 --
  workflows: {
    byType: (ws: string, typeId: string) => ["workspace", ws, "workflows", typeId] as const,
    byRole: (ws: string, typeId: string, role: string) =>
      ["workspace", ws, "workflows", typeId, "role", role] as const,
    byWorkitem: (ws: string, workitemId: string, role: string) =>
      ["workspace", ws, "workflows", "workitem", workitemId, role] as const,
  },

  // -- 프로젝트 --
  projects: {
    all: (ws: string) => ["workspace", ws, "projects"] as const,
    /** 삭제 가능한 프로젝트 id(목록 선택 체크박스) — all 프리픽스 하위라 목록 무효화 시 함께 갱신. */
    deletable: (ws: string) => ["workspace", ws, "projects", "deletable"] as const,
    /** 이 프로젝트를 사용 중인 기록 번호(삭제 확인창). */
    references: (ws: string, projectId: string) =>
      ["workspace", ws, "projects", projectId, "references"] as const,
  },

  // -- 결재선 기본값 (approval_line_defaults) --
  approvalAbsences: {
    all: (ws: string) => ["workspace", ws, "approvalAbsences"] as const,
  },
  approvalLineDefaults: {
    all: (ws: string) => ["workspace", ws, "approvalLineDefaults"] as const,
  },

  // -- 프로젝트 템플릿 (project_templates) --
  projectTemplates: {
    all: (ws: string) => ["workspace", ws, "projectTemplates"] as const,
  },

  // -- 마스터 데이터 --
  products: {
    all: (companySlug: string) => ["company", companySlug, "products"] as const,
  },
  departments: {
    all: (companySlug: string) => ["company", companySlug, "departments"] as const,
  },

  // -- KPI/대시보드 --
  kpi: {
    dashboard: (ws: string) => ["workspace", ws, "kpi", "dashboard"] as const,
  },

  // -- 맞춤 대시보드 프리셋 (워크스페이스 범위 config + per-user 활성 선택) --
  dashPresets: {
    all: (ws?: string) => ["dashPresets", ws ?? ""] as const,
    /** 업무유형의 단계 화면 배치 필드 키(대시보드 필터 열 한정). */
    placedFields: (ws: string | undefined, typeId: string) =>
      ["dashPresets", ws ?? "", "placedFields", typeId] as const,
    layout: (ws: string | undefined, scope: string) =>
      ["dashPresets", ws ?? "", "layout", scope] as const,
  },

  // -- 사이드바 네비게이션 구조 (nav_sections · nav_entries) --
  nav: {
    all: (ws: string) => ["workspace", ws, "nav"] as const,
  },
} as const;
